import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  MoreHorizontal,
  Users,
  ArrowLeft,
  Crown,
  Loader2,
  Palette,
  Lock,
  Sparkles,
  LayoutGrid,
  List,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { contactsApi } from "@/services/api";
import { generateUUID } from "@/utils/transform";

// Predefined colors for groups
const GROUP_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#3b82f6", // blue
  "#64748b", // slate
];

export const ContactGroupsPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // State
  const [viewMode, setViewMode] = useState("grid");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: GROUP_COLORS[0],
  });

  // Queries
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ["contact-groups"],
    queryFn: () => contactsApi.listGroups(),
  });

  const { data: limitsData } = useQuery({
    queryKey: ["contact-limits"],
    queryFn: () => contactsApi.getLimits(),
  });

  const { data: membersData, isLoading: isLoadingMembers } = useQuery({
    queryKey: ["group-members", selectedGroup?.id],
    queryFn: () => contactsApi.getGroupMembers(selectedGroup.id),
    enabled: !!selectedGroup && isMembersDialogOpen,
  });

  const groups = groupsData?.data?.data || [];
  const limits = limitsData?.data?.data || null;
  const members = membersData?.data?.data || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => contactsApi.createGroup(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      queryClient.invalidateQueries({ queryKey: ["contact-limits"] });
      toast.success(
        t("contacts.groupCreateSuccess", "Group created successfully"),
      );
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.groupCreateError", "Failed to create group"),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => contactsApi.updateGroup(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      toast.success(
        t("contacts.groupUpdateSuccess", "Group updated successfully"),
      );
      setIsEditDialogOpen(false);
      setSelectedGroup(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.groupUpdateError", "Failed to update group"),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => contactsApi.deleteGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      queryClient.invalidateQueries({ queryKey: ["contact-limits"] });
      toast.success(
        t("contacts.groupDeleteSuccess", "Group deleted successfully"),
      );
      setIsDeleteDialogOpen(false);
      setSelectedGroup(null);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.groupDeleteError", "Failed to delete group"),
      );
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ groupId, contactId }) =>
      contactsApi.removeFromGroup(groupId, contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["group-members", selectedGroup?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      toast.success(t("contacts.memberRemoved", "Member removed from group"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error ||
          t("contacts.memberRemoveError", "Failed to remove member"),
      );
    },
  });

  // Helpers
  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: GROUP_COLORS[0],
    });
  };

  const openEditDialog = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || "",
      color: group.color || GROUP_COLORS[0],
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (group) => {
    setSelectedGroup(group);
    setIsDeleteDialogOpen(true);
  };

  const openMembersDialog = (group) => {
    setSelectedGroup(group);
    setIsMembersDialogOpen(true);
  };

  const handleCreate = () => {
    if (!formData.name) {
      toast.error(t("contacts.groupNameRequired", "Group name is required"));
      return;
    }
    createMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!formData.name) {
      toast.error(t("contacts.groupNameRequired", "Group name is required"));
      return;
    }
    updateMutation.mutate({ id: selectedGroup.id, data: formData });
  };

  const handleDelete = () => {
    deleteMutation.mutate(selectedGroup.id);
  };

  const handleRemoveMember = (contactId) => {
    removeMemberMutation.mutate({ groupId: selectedGroup.id, contactId });
  };

  // Check limits
  const canCreateGroups = Boolean(
    limits &&
    limits.groups &&
    (limits.groups.limit === -1 || limits.groups.limit > 0),
  );
  const isAtGroupLimit = Boolean(
    limits &&
    limits.groups &&
    limits.groups.limit !== -1 &&
    limits.groups.remaining === 0,
  );

  const renderContactsGroupGridOrList = () => {
    if (isLoadingGroups) {
      return viewMode === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(4)].map(() => (
            <Card key={generateUUID()}>
              <CardHeader>
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-1/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="p-6 space-y-4">
              {[...Array(4)].map(() => (
                <div key={generateUUID()} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/4" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }
    if (groups.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mx-auto mb-4">
              <FolderOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-1">
              {t("contacts.noGroups", "No groups yet")}
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              {t(
                "contacts.createFirstGroup",
                "Create your first group to organize your contacts",
              )}
            </p>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("contacts.createGroup", "Create Group")}
            </Button>
          </CardContent>
        </Card>
      );
    }
    if (viewMode === "grid") {
      return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {groups.map((group) => (
            <Card key={group.id} className="group overflow-hidden">
              <div
                className="h-24 flex items-center justify-center relative"
                style={{ backgroundColor: `${group.color}15` }}
              >
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${group.color}25` }}
                >
                  <FolderOpen
                    className="h-7 w-7"
                    style={{ color: group.color }}
                  />
                </div>
                <div className="absolute top-2 right-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => openMembersDialog(group)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        {t("contacts.viewMembers", "View Members")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(group)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("common.edit", "Edit")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => openDeleteDialog(group)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("common.delete", "Delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-medium truncate mb-1">{group.name}</h3>
                {group.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {group.description}
                  </p>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>
                    {t("contacts.memberCount", "{{count}} members", {
                      count: group.memberCount || 0,
                    })}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    return (
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("contacts.group", "Group")}</TableHead>
              <TableHead>{t("contacts.description", "Description")}</TableHead>
              <TableHead>{t("contacts.members", "Members")}</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${group.color}20` }}
                    >
                      <FolderOpen
                        className="h-5 w-5"
                        style={{ color: group.color }}
                      />
                    </div>
                    <span className="font-medium">{group.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-muted-foreground line-clamp-1">
                    {group.description || "-"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{group.memberCount || 0}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => openMembersDialog(group)}
                      >
                        <Users className="h-4 w-4 mr-2" />
                        {t("contacts.viewMembers", "View Members")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(group)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("common.edit", "Edit")}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => openDeleteDialog(group)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("common.delete", "Delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    );
  };

  const renderMembers = () => {
    if (isLoadingMembers) {
      return (
        <div className="space-y-2">
          {[...Array(3)].map(() => (
            <Skeleton key={generateUUID()} className="h-12 w-full" />
          ))}
        </div>
      );
    }

    return members.length === 0 ? (
      <div className="py-8 text-center text-muted-foreground">
        <Users className="h-8 w-8 mx-auto mb-2" />
        <p>{t("contacts.noMembers", "No contacts in this group yet")}</p>
        <p className="text-sm">
          {t(
            "contacts.addMembersHint",
            "Add contacts to this group from the Contacts page",
          )}
        </p>
      </div>
    ) : (
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {members.map((contact) => (
          <div
            key={contact.id}
            className="flex items-center justify-between p-3 border rounded-lg"
          >
            <div>
              <p className="font-medium">{contact.name || contact.email}</p>
              {contact.name && (
                <p className="text-sm text-muted-foreground">{contact.email}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemoveMember(contact.id)}
              disabled={removeMemberMutation.isPending}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>
    );
  };

  // If user doesn't have groups feature, show upgrade message
  if (!canCreateGroups) {
    return (
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("contacts.groupsTitle", "Contact Groups")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "contacts.groupsDescription",
              "Organize your contacts into groups for easier management",
            )}
          </p>
        </div>

        {/* Upgrade Required Card */}
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
              <Lock className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">
              {t("upgrade.featureNotAvailable", "Feature Not Available")}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md">
              {t(
                "upgrade.groupsUpgradeDesc",
                "Contact groups help you organize recipients for faster envelope creation. Upgrade your plan to unlock this feature.",
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/settings/billing")}>
                <Sparkles className="mr-2 h-4 w-4" />
                {t("upgrade.upgradePlan", "Upgrade Plan")}
              </Button>
              <Button variant="outline" onClick={() => navigate("/contacts")}>
                {t("contacts.backToContacts", "Back to Contacts")}
              </Button>
            </div>

            {/* Feature highlights */}
            <div className="mt-8 pt-8 border-t w-full max-w-lg">
              <div className="w-full flex items-center justify-center gap-2 mb-4">
                <FolderOpen className="h-3.5 w-3.5 text-primary" />
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t("upgrade.withGroups", "With Contact Groups you can:")}
                </h3>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
                <li>
                  {t(
                    "upgrade.groupsFeature1",
                    "Organize contacts by department, project, or client",
                  )}
                </li>
                <li>
                  {t(
                    "upgrade.groupsFeature2",
                    "Quickly add all group members as recipients",
                  )}
                </li>
                <li>
                  {t(
                    "upgrade.groupsFeature3",
                    "Color-code groups for visual organisation",
                  )}
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header with back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/contacts"
          className="flex items-center justify-center h-8 w-8 rounded-lg border hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("contacts.groupsTitle", "Contact Groups")}
          </h1>
          <p className="text-muted-foreground">
            {t(
              "contacts.groupsDescription",
              "Organize your contacts into groups for easier management",
            )}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        {/* View toggle */}
        <div className="flex items-center border rounded-lg p-1">
          <Button
            variant={viewMode === "grid" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("grid")}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2"
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  disabled={isAtGroupLimit}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("contacts.createGroup", "Create Group")}
                </Button>
              </span>
            </TooltipTrigger>
            {isAtGroupLimit && (
              <TooltipContent>
                <p>
                  {t(
                    "contacts.groupLimitReached",
                    "Group limit reached. Upgrade your plan.",
                  )}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Limits Card */}
      {limits && limits.groups && limits.groups.limit > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("contacts.groupUsage", "Group Usage")}
                </p>
                <p className="text-lg font-semibold">
                  {limits.groups.current} / {limits.groups.limit}
                </p>
              </div>
              {isAtGroupLimit && (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/settings/billing/plans">
                    <Crown className="h-4 w-4 mr-2" />
                    {t("common.upgrade", "Upgrade")}
                  </Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Groups Grid/List */}
      {renderContactsGroupGridOrList()}

      {/* Create/Edit Dialog */}
      <Dialog
        open={isCreateDialogOpen || isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedGroup(null);
            resetForm();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditDialogOpen
                ? t("contacts.editGroup", "Edit Group")
                : t("contacts.createGroup", "Create Group")}
            </DialogTitle>
            <DialogDescription>
              {isEditDialogOpen
                ? t("contacts.editGroupDescription", "Update the group details")
                : t(
                    "contacts.createGroupDescription",
                    "Create a new group to organize your contacts",
                  )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("contacts.groupName", "Group Name")} *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder={t(
                  "contacts.groupNamePlaceholder",
                  "e.g., Clients, Partners, Team",
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">
                {t("contacts.description", "Description")}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t(
                  "contacts.groupDescriptionPlaceholder",
                  "What is this group for?",
                )}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t("contacts.color", "Color")}
              </Label>
              <div className="flex flex-wrap gap-2">
                {GROUP_COLORS.map((color) => (
                  <button
                    key={color}
                    aria-label="Color"
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={isEditDialogOpen ? handleUpdate : handleCreate}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {isEditDialogOpen
                ? t("common.save", "Save")
                : t("common.create", "Create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("contacts.deleteGroup", "Delete Group")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "contacts.deleteGroupConfirm",
                "Are you sure you want to delete this group? Contacts in this group will not be deleted.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t("common.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedGroup && (
                <>
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: selectedGroup.color }}
                  />
                  {selectedGroup.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {t("contacts.groupMembers", "Contacts in this group")}
            </DialogDescription>
          </DialogHeader>
          {renderMembers()}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsMembersDialogOpen(false)}
            >
              {t("common.close", "Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactGroupsPage;
